import React from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import "./JobNotificationToast.css";

interface JobNotificationToastProps {
  title: string;
  message: React.ReactNode;
  onClick?: () => void;
  onClose: () => void;
  icon?: React.ReactNode;
  actionLabel?: string;
}

/**
 * Glassy green banner notification, floating above the chatbot FAB.
 * No grid/check background — clean green glass with pill actions.
 */
const JobNotificationToast: React.FC<JobNotificationToastProps> = ({
  title,
  message,
  onClick,
  onClose,
  icon,
  actionLabel = "Review",
}) => {
  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="job-notif-banner"
      role={onClick ? "button" : "status"}
      onClick={onClick}
    >
      <div className="job-notif-banner-left">
        {icon && <div className="job-notif-banner-icon">{icon}</div>}
        <p className="job-notif-banner-text">
          <span className="job-notif-banner-title">{title}</span>
          {" — "}
          <span>{message}</span>
        </p>
      </div>
      <div className="job-notif-banner-actions">
        {onClick && (
          <button
            type="button"
            className="job-notif-banner-action"
            onClick={handleAction}
          >
            {actionLabel}
          </button>
        )}
        <button
          type="button"
          className="job-notif-banner-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Dismiss notification"
        >
          <X size={18} />
        </button>
      </div>
    </motion.div>,
    document.body
  );
};

export default JobNotificationToast;
